import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Trash2, Webhook as WebhookIcon } from 'lucide-react'
import { useGuildStore } from '@/store/guilds'
import { guildApi } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { displayName } from '@/store/social'
import { cx } from '@/lib/cx'
import { ChannelType, type Guild, type Webhook } from '@/types/api'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import layer from '../settings/SettingsLayer.module.css'
import gs from './GuildSettingsLayer.module.css'
import styles from './WebhooksSection.module.css'
import { Select } from '../Select'

/** Адрес вебхука для внешних сервисов; API отдаёт url с внешним адресом, иначе собираем от текущего origin. */
function webhookUrl(w: Webhook): string {
  return w.url ?? `${location.origin}/api/v1/webhooks/${w.id}/${w.token ?? ''}`
}

/** Настройки сервера → «Вебхуки»: создание в текстовом канале, перенос, переименование, ссылка, удаление. */
export function WebhooksSection({ guild }: { guild: Guild }) {
  const qc = useQueryClient()
  const channels = useGuildStore((s) => s.channels[guild.id])
  const textChannels = useMemo(
    () => Object.values(channels ?? {}).filter((c) => c.type === ChannelType.GuildText).sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id.localeCompare(b.id)),
    [channels],
  )
  const channelOptions = textChannels.map((c) => ({ value: c.id, label: `#${c.name}` }))
  const hooks = useQuery({ queryKey: ['webhooks', guild.id], queryFn: () => guildApi.webhooks(guild.id) })
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [channelId, setChannelId] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<string | null>(null)
  const refresh = () => void qc.invalidateQueries({ queryKey: ['webhooks', guild.id] })
  const fail = (err: unknown) => {
    const d = describeError(err)
    setError(d.fields.name ?? d.fields.channel_id ?? d.message)
  }
  const create = useMutation({ mutationFn: (v: { channelId: string; name: string }) => guildApi.createWebhook(v.channelId, v.name), onSuccess: () => { setName(''); refresh() }, onError: fail })
  const update = useMutation({ mutationFn: (v: { id: string; patch: { name?: string; channel_id?: string } }) => guildApi.updateWebhook(v.id, v.patch), onSuccess: refresh, onError: fail })
  const remove = useMutation({ mutationFn: (id: string) => guildApi.deleteWebhook(id), onSuccess: () => { setConfirm(null); refresh() }, onError: fail })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const target = channelId || textChannels[0]?.id
    if (!target || name.trim().length < 1) return
    setError(null)
    create.mutate({ channelId: target, name: name.trim() })
  }
  const copy = async (w: Webhook) => {
    try {
      await navigator.clipboard.writeText(webhookUrl(w))
      setCopied(w.id)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setError('Не удалось скопировать: разрешите доступ к буферу обмена')
    }
  }

  const list = hooks.data ?? []
  return (
    <section>
      <h1 className={layer.sectionTitle}>Вебхуки</h1>
      <p className={layer.text}>Вебхук — адрес, на который внешний сервис шлёт сообщения в канал.</p>
      <ul className={layer.points}>
        <li>формат Discord: <code className={gs.code}>POST {'{url}'}</code> с телом <code className={gs.code}>{'{"content": "…", "embeds": […]}'}</code></li>
        <li>для GitHub добавьте к адресу <code className={gs.code}>/github</code>, для Slack — <code className={gs.code}>/slack</code></li>
        <li>до 15 вебхуков на канал, 30 сообщений в минуту на вебхук</li>
      </ul>
      <form className={styles.createForm} onSubmit={submit} aria-label="Новый вебхук">
        <Field label="Название" htmlFor="wh-name" hint="Так подписываются сообщения, если сервис не передал своё имя">
          <input id="wh-name" className={auth.input} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Например, CI" />
        </Field>
        <Field label="Канал" htmlFor="wh-channel">
          <Select id="wh-channel" value={channelId || textChannels[0]?.id || ''} options={channelOptions} onChange={setChannelId} />
        </Field>
        <div className={layer.buttonRow}>
          <button type="submit" className={layer.button} disabled={create.isPending || !name.trim() || textChannels.length === 0}>{create.isPending ? 'Создаём…' : 'Создать вебхук'}</button>
        </div>
      </form>
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      {hooks.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {!hooks.isPending && list.length === 0 ? <p className={layer.text}>Вебхуков пока нет.</p> : null}
      <ul className={styles.list}>
        {list.map((w) => (
          <li key={w.id} className={styles.row}>
            <span className={styles.icon} aria-hidden="true"><WebhookIcon size={20} /></span>
            <div className={styles.main}>
              <input
                className={styles.name}
                defaultValue={w.name}
                maxLength={80}
                aria-label="Название вебхука"
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== w.name) update.mutate({ id: w.id, patch: { name: v } }) }}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
              />
              <span className={styles.meta}>
                {w.user ? `Создал(а) ${displayName(w.user)}` : w.application_id ? 'Создано ботом' : ''}
              </span>
              <label className={styles.channel}>
                <span>Канал</span>
                <Select size="sm" value={w.channel_id} options={channelOptions} onChange={(v) => update.mutate({ id: w.id, patch: { channel_id: v } })} label={`Канал вебхука ${w.name}`} />
              </label>
            </div>
            <div className={styles.actions}>
              <button type="button" className={gs.smallButton} onClick={() => void copy(w)}><Copy size={14} /> {copied === w.id ? 'Скопировано' : 'Копировать URL'}</button>
              {confirm === w.id ? (
                <>
                  <button type="button" className={gs.smallDanger} disabled={remove.isPending} onClick={() => remove.mutate(w.id)}>Точно удалить</button>
                  <button type="button" className={gs.smallButton} onClick={() => setConfirm(null)}>Отмена</button>
                </>
              ) : (
                <button type="button" className={cx(gs.smallButton, gs.smallDanger)} onClick={() => setConfirm(w.id)} aria-label={`Удалить вебхук ${w.name}`} data-tooltip="Удалить"><Trash2 size={14} /></button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
